// Money Passbook quiz — questions, scoring model and result copy.
// Every option carries a `score` (0–1) that feeds the Financial Well-Being Check.
// Trait options also carry a `trait` that feeds the Money Personality.

export const AREAS = {
  management: { key: "management", label: "Money Management" },
  knowledge: { key: "knowledge", label: "Financial Knowledge" },
  mindset: { key: "mindset", label: "Money Mindset" },
  protection: { key: "protection", label: "Financial Protection" },
};

export const NEXT_ACTIONS = {
  management: "Track every expense for the next 30 days and set one clear spending limit.",
  knowledge: "Spend 20 minutes learning how compounding and risk/return work.",
  mindset: "Write down one financial goal with a date and a monthly amount attached.",
  protection: "Build or review your emergency fund — aim for 3–6 months of essentials.",
};

export const QUESTIONS = [
  // ---- Spending behaviour → Money Management
  {
    section: "Spending Behaviour",
    area: "management",
    type: "trait",
    text: "An unexpected ₹5,000 bonus lands in your account. What's your first instinct?",
    options: [
      { label: "Buy something I've been eyeing immediately", trait: "impulse", score: 0.25 },
      { label: "Move it straight into savings, untouched", trait: "saver", score: 0.75 },
      { label: "Split it — some spent, some saved, some invested", trait: "planner", score: 1 },
      { label: "Feel a bit stressed about what to 'do' with it", trait: "avoider", score: 0.25 },
    ],
  },
  {
    section: "Spending Behaviour",
    area: "management",
    type: "trait",
    text: "You spot a 50% off sale on something you don't really need.",
    options: [
      { label: "Buy it — a deal like this won't come again", trait: "impulse", score: 0.25 },
      { label: "Walk away, the discount doesn't matter if it's unneeded", trait: "saver", score: 1 },
      { label: "Add it to a wishlist and revisit in a week", trait: "planner", score: 0.9 },
      { label: "Avoid the store/app entirely to sidestep the decision", trait: "avoider", score: 0.5 },
    ],
  },
  {
    section: "Spending Behaviour",
    area: "management",
    type: "trait",
    text: "How closely do you track your monthly expenses?",
    options: [
      { label: "I don't — I'll deal with it if a problem shows up", trait: "impulse", score: 0.1 },
      { label: "Very closely, I note down almost everything", trait: "saver", score: 0.9 },
      { label: "I use an app or sheet and review it monthly", trait: "planner", score: 1 },
      { label: "I know I should, but I avoid opening my statements", trait: "avoider", score: 0.1 },
    ],
  },
  {
    section: "Spending Behaviour",
    area: "management",
    type: "trait",
    text: "Dinner with friends — the bill gets split unevenly in your disfavour. What do you do?",
    options: [
      { label: "Pay it and not think twice, it's not a big deal", trait: "impulse", score: 0.4 },
      { label: "Feel uneasy about it but stay quiet to avoid awkwardness", trait: "avoider", score: 0.25 },
      { label: "Speak up calmly and ask for a fair split", trait: "planner", score: 1 },
      { label: "Pay quietly but mentally note it for next time", trait: "saver", score: 0.6 },
    ],
  },

  // ---- Knowledge → Financial Knowledge / Financial Protection
  {
    section: "Money Knowledge",
    area: "knowledge",
    type: "knowledge",
    text: "If you invest ₹10,000 at 5% simple interest per year for 2 years, how much interest do you earn?",
    options: [
      { label: "₹500", correct: false },
      { label: "₹1,000", correct: true },
      { label: "₹1,050", correct: false },
      { label: "₹1,500", correct: false },
    ],
    explain:
      "Simple Interest = (Principal × Rate × Time) ÷ 100 = (10,000 × 5 × 2) ÷ 100 = ₹1,000. Simple interest is calculated only on the original principal each year — it doesn't compound.",
  },
  {
    section: "Money Knowledge",
    area: "knowledge",
    type: "knowledge",
    text: "Which of these typically carries the highest risk — and the highest potential return?",
    options: [
      { label: "Savings account", correct: false },
      { label: "Fixed deposit", correct: false },
      { label: "Government bond", correct: false },
      { label: "Equity / stocks", correct: true },
    ],
    explain:
      "Risk and return generally move together. Equities can grow (or fall) much faster than FDs or bonds, since your money is tied to business performance rather than a fixed, guaranteed rate.",
  },
  {
    section: "Money Knowledge",
    area: "protection",
    type: "knowledge",
    text: "How many months of essential expenses do most financial planners recommend keeping in an emergency fund?",
    options: [
      { label: "About 1 month", correct: false },
      { label: "3–6 months", correct: true },
      { label: "12+ months", correct: false },
      { label: "None — a credit card covers it", correct: false },
    ],
    explain:
      "3–6 months of essential expenses is the standard benchmark. It's enough runway to handle a job loss or medical emergency without derailing long-term investments or going into high-interest debt.",
  },

  // ---- Attitude → Money Mindset
  {
    section: "Attitude Towards Money",
    area: "mindset",
    type: "trait",
    text: "When you think about your overall finances, you mostly feel...",
    options: [
      { label: "Excited — money means options and experiences", trait: "impulse", score: 0.5 },
      { label: "Anxious, like something might go wrong", trait: "avoider", score: 0.2 },
      { label: "In control, I know roughly where I stand", trait: "planner", score: 1 },
      { label: "Cautious — I'd rather have more cushion than less", trait: "saver", score: 0.75 },
    ],
  },
  {
    section: "Attitude Towards Money",
    area: "mindset",
    type: "trait",
    text: "For you, money is mainly a tool for...",
    options: [
      { label: "Freedom and experiences in the moment", trait: "impulse", score: 0.5 },
      { label: "Security and peace of mind", trait: "saver", score: 0.8 },
      { label: "Building toward long-term goals", trait: "planner", score: 1 },
      { label: "Honestly, it's more a source of stress", trait: "avoider", score: 0.2 },
    ],
  },
  {
    section: "Attitude Towards Money",
    area: "mindset",
    type: "trait",
    text: "Do you set financial goals?",
    options: [
      { label: "Not really, I take things as they come", trait: "impulse", score: 0.2 },
      { label: "I have a rough idea, nothing written down", trait: "saver", score: 0.5 },
      { label: "Yes, clear goals that I actively track", trait: "planner", score: 1 },
      { label: "I've tried, but I never really follow through", trait: "avoider", score: 0.3 },
    ],
  },
  {
    section: "Attitude Towards Money",
    area: "mindset",
    type: "trait",
    text: "Someone brings up investing at a gathering. Your instinct?",
    options: [
      { label: "I'm curious — tell me more, what's working for you?", trait: "planner", score: 0.8 },
      { label: "I already invest, so I'll swap notes", trait: "saver", score: 1 },
      { label: "I feel a bit out of my depth", trait: "avoider", score: 0.3 },
      { label: "I'd rather talk about literally anything else", trait: "impulse", score: 0.2 },
    ],
  },

  // ---- Well-being → Financial Protection
  {
    section: "Financial Well-Being",
    area: "protection",
    type: "trait",
    text: "If you lost your income tomorrow, how would you feel about covering your expenses and your future retirement plans?",
    options: [
      { label: "I don't really think about it until it happens", trait: "impulse", score: 0.2 },
      { label: "I'd manage for a while, but it would eat into everything I have", trait: "saver", score: 0.6 },
      { label: "Confident — I have an emergency fund and retirement savings on track", trait: "planner", score: 1 },
      { label: "I'd need to borrow or lean on someone almost immediately", trait: "avoider", score: 0 },
    ],
  },
];

export const PERSONALITIES = {
  impulse: {
    name: "The Spontaneous Spender",
    desc: "Your responses suggest you currently lean towards spending in the moment. Money tends to feel exciting and social, and pausing or tracking before a purchase may not yet be a habit.",
    tips: [
      "Try the 24-hour rule: wait a day before any non-essential purchase over a set amount.",
      "Open a separate 'guilt-free fun money' account so spending doesn't eat into savings.",
      "Do a 10-minute expense check-in once a week, not once a year.",
    ],
  },
  saver: {
    name: "The Cautious Saver",
    desc: "Your responses suggest you currently lean towards caution and discipline. Overspending is rarely the issue — but some of that carefulness may mean money sits idle instead of working for you.",
    tips: [
      "Automate a small SIP so growth happens without a fresh decision each month.",
      "Learn the basics of low-risk mutual funds — safety and stagnation aren't the same thing.",
      "Allow a small, guilt-free spending budget each month — it's not a leak, it's balance.",
    ],
  },
  planner: {
    name: "The Balanced Planner",
    desc: "Your responses suggest you currently lean towards tracking, goal-setting and feeling broadly in control of your money. The opportunity from here tends to be refinement rather than repair.",
    tips: [
      "Review your asset allocation once a year as your income and goals shift.",
      "As income grows, increase your SIP amount rather than only your spending.",
      "Explore tax-efficient instruments to make your existing discipline go further.",
    ],
  },
  avoider: {
    name: "The Financial Postponer",
    desc: "Your responses suggest you currently lean towards putting money decisions off. That can feel protective in the short term, but it often keeps you from feeling more in control.",
    tips: [
      "Start with just one simple expense tracker — visibility reduces stress more than willpower does.",
      "Break any big goal into one small monthly action instead of one big overwhelming plan.",
      "Consider talking to a financial coach — accountability makes postponing much harder to default to.",
    ],
  },
};

const TRAIT_TIEBREAK = ["planner", "saver", "impulse", "avoider"];
const WEAK_TIEBREAK = ["protection", "management", "knowledge", "mindset"];

const optionScore = (q, opt) => (q.type === "knowledge" ? (opt.correct ? 1 : 0) : opt.score);

// answers: array of selected option indices, one per question.
export const computeResult = (answers) => {
  const traitCounts = { impulse: 0, saver: 0, planner: 0, avoider: 0 };
  const areaSum = {};
  const areaMax = {};
  let total = 0;
  let knowledgeCorrect = 0;

  QUESTIONS.forEach((q, i) => {
    const opt = q.options[answers[i]];
    const s = optionScore(q, opt);
    total += s;
    areaSum[q.area] = (areaSum[q.area] || 0) + s;
    areaMax[q.area] = (areaMax[q.area] || 0) + 1;
    if (q.type === "trait") traitCounts[opt.trait] += 1;
    else if (opt.correct) knowledgeCorrect += 1;
  });

  let personalityKey = TRAIT_TIEBREAK[0];
  TRAIT_TIEBREAK.forEach((t) => {
    if (traitCounts[t] > traitCounts[personalityKey]) personalityKey = t;
  });

  const areas = Object.keys(AREAS).map((key) => ({
    key,
    label: AREAS[key].label,
    pct: Math.round((areaSum[key] / areaMax[key]) * 100),
  }));

  const strongest = [...areas].sort((a, b) => b.pct - a.pct)[0];
  const weakest = [...areas].sort(
    (a, b) => a.pct - b.pct || WEAK_TIEBREAK.indexOf(a.key) - WEAK_TIEBREAK.indexOf(b.key)
  )[0];

  return {
    personality_key: personalityKey,
    personality: PERSONALITIES[personalityKey].name,
    score: Math.round((total / QUESTIONS.length) * 10),
    knowledge_correct: knowledgeCorrect,
    areas,
    strongest_area: strongest.label,
    weakest_area: weakest.label,
    next_action: NEXT_ACTIONS[weakest.key],
  };
};
